import { useState } from "react";
import { property } from "../data/property";
import { Play, Layout, ExternalLink, X } from "lucide-react";
import { trackEvent } from "../lib/analytics";

export default function FloorplanVideo() {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <section
      id="plattegrond"
      data-testid="floorplan-video-section"
      className="bg-brand-surface py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl">
          <p className="overline">Bekijk in detail</p>
          <h2 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight text-brand-ink sm:text-5xl lg:text-6xl text-balance">
            Plattegronden & <span className="italic">videotour</span>.
          </h2>
          <p className="mt-6 max-w-xl text-lg text-brand-muted">
            Bekijk de indeling van alle drie de woonlagen en stap virtueel naar binnen.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Floorplan iframe */}
          <div className="lg:col-span-8">
            <div className="relative aspect-[4/3] w-full overflow-hidden border border-brand-border bg-brand-bg lg:aspect-[16/10]">
              <iframe
                data-testid="floorplan-iframe"
                src={property.floorplanEmbed}
                title="Interactieve plattegrond Kempenaar 29"
                className="absolute inset-0 h-full w-full"
                loading="lazy"
                allowFullScreen
              />
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-brand-muted">
              <span className="inline-flex items-center gap-2">
                <Layout size={12} />
                Interactieve 2D / 3D plattegrond · klik en sleep
              </span>
              <a
                data-testid="floorplan-funda-link"
                href="https://www.funda.nl/detail/koop/wijk-bij-duurstede/huis-kempenaar-29/44486481/media/plattegronden"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("outbound_click", { location: "floorplan", destination: "funda" })}
                className="inline-flex items-center gap-1 text-brand-moss hover:opacity-70"
              >
                Open op Funda <ExternalLink size={11} />
              </a>
            </div>
          </div>

          {/* Video card */}
          <div className="lg:col-span-4">
            <button
              data-testid="video-play-btn"
              onClick={() => { trackEvent("video_play", { video: "funda_tour" }); setVideoOpen(true); }}
              className="group relative block aspect-[4/5] w-full overflow-hidden border border-brand-border lg:aspect-auto lg:h-full"
            >
              <img
                src={property.videoThumb}
                alt="Videotour Kempenaar 29"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30 transition-colors group-hover:from-black/60" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/10 backdrop-blur-md ring-1 ring-white/40 transition-all group-hover:scale-110 group-hover:bg-white group-hover:text-brand-moss">
                  <Play size={28} className="ml-1" strokeWidth={1.5} fill="currentColor" />
                </div>
                <p className="mt-6 font-serif text-2xl">Bekijk de videotour</p>
                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-white/80">
                  Stap virtueel naar binnen
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Video modal */}
      {videoOpen && (
        <div
          data-testid="video-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
          onClick={() => setVideoOpen(false)}
        >
          <button
            data-testid="video-close"
            onClick={() => setVideoOpen(false)}
            className="absolute right-6 top-6 text-white hover:opacity-70"
          >
            <X size={32} />
          </button>
          <div
            className="flex w-full max-w-4xl flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-video w-full overflow-hidden border border-white/10">
              <img
                src={property.videoThumb}
                alt="Videotour preview"
                className="h-full w-full object-cover"
              />
            </div>
            <p className="mt-6 text-center text-white">
              De videotour wordt gehost op Funda. Klik hieronder om af te spelen.
            </p>
            <a
              data-testid="video-funda-link"
              href={property.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("outbound_click", { location: "video_modal", destination: "funda" })}
              className="mt-6 inline-flex items-center gap-2 bg-white px-8 py-4 text-sm font-medium text-brand-ink hover:bg-brand-bg"
            >
              <Play size={16} fill="currentColor" />
              Open videotour op Funda
              <ExternalLink size={14} />
            </a>
          </div>
        </div>
      )}
    </section>
  );
}
